import { createFileRoute } from "@tanstack/react-router";
import { useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import type { User } from "@/store/User";
import PhoneNumberField from "@/components/ui/form/phone-number-field";
import { cn } from "@/lib/utils";
import {
  InputField,
  LightSelectField,
  SelectField,
} from "@/components/ui/form";
import Show from "@/components/ui/show";
import SelectUser from "@/components/user/select-user";
import ToggleSwitch from "@/components/ui/form/toggle-switch";
import {
  EmployeeRoleOptions,
  EmployeeStatusOptions,
} from "@/components/employee/employee-meta";
import PageHeader from "@/components/ui/shared/page-header";

export const Route = createFileRoute("/(dasboard)/employees/create")({
  component: () => <CreateEmployeePage />,
});

function CreateEmployeePage() {
  const store = useAppStore();
  const { t } = useTranslate();

  // const form = useSimpleForm({
  //   user_id: "test-user-001",
  //   first_name: "John",
  //   last_name: "Doe",
  //   email: "john.doe@example.com",
  //   phone_number: "+237699123456",
  //   role: "seller", // could be 'admin', 'manager', etc.
  //   hire_date: "2025-11-15", // YYYY-MM-DD format
  //   status: "active", // 'active', 'inactive', 'terminated'
  //   description: "Test employee for frontend testing",
  //   date_of_birth: "1990-05-20",
  //   employment_status: "full-time", // 'full-time', 'part-time', 'contract', 'intern'
  //   emergency_contact: "+237699654321",
  //   salary: "350000", // string or number depending on your form setup
  //   created_at: new Date().toISOString(),
  //   updated_at: new Date().toISOString(),
  // });

  const form = useSimpleForm({
    user_id: undefined,
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "+237",
    password: "",
    role: "seller",
    hire_date: new Date().toISOString().split("T")[0],
    status: "active",
    description: "",
    date_of_birth: "",
    employment_status: "full-time",
    emergency_contact: "",
    salary: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  // Translate status options
  const translatedStatusOptions = EmployeeStatusOptions.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // Employment status options
  const employmentStatusOptions = [
    { value: "full-time", label: t("Full-time") },
    { value: "part-time", label: t("Part-time") },
    { value: "contract", label: t("Contract") },
    { value: "intern", label: t("Intern") },
  ];

  const [useExistingUser, setUseExistingUser] = useState<Boolean>(false);
  const [user, setUser] = useState<User>();

  const submit = async () => {
    store.errors.reset();

    store.employee
      .create({
        ...form.values,
        shop_id: store.auth?.session?.shop?.id,
      })
      .then(() => {
        form.reset();
        window.history.back();
      })
      .catch(store.errors.catch);
  };

  return (
    <section className="max-w-4xl mx-auto">
      <PageHeader title="Register an employee" />

      <>{store.errors.render()}</>

      <div className="bg-white p-6 rounded-md border-gray-200">
        <h3 className="font-semibold">{t("User informations")}</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <ToggleSwitch
              label="Use an existing user"
              checked={useExistingUser}
              onChange={(checked: boolean) => {
                setUseExistingUser(checked);
                if (!checked) {
                  form.setValue("user_id", undefined);
                  setUser(undefined);
                }
              }}
            />
          </div>

          <Show when={!useExistingUser}>
            <div className="col-span-1 md:col-span-2 bg-gray-200 p-4 rounded-md">
              {t(
                "A user account will be create for this employee with the default password. Later the user will be able to change that default password"
              )}
            </div>
            <InputField
              label={t("First Name")}
              name="first_name"
              value={form.values.first_name}
              onChange={form.handleChange}
              error={store.errors.values.first_name}
            />
            <InputField
              label={t("Last Name")}
              name="last_name"
              value={form.values.last_name}
              onChange={form.handleChange}
              error={store.errors.values.last_name}
            />
            <InputField
              label={t("Email")}
              name="email"
              type="email"
              value={form.values.email}
              onChange={form.handleChange}
              error={store.errors.values.email}
            />
            <div>
              <label
                className={cn("mb-1.5 block text-sm font-medium text-gray-900")}
              >
                {t("Phone number")}
              </label>
              <PhoneNumberField
                value={form.values.phone_number}
                onValueChange={(value: string) => {
                  form.setValue("phone_number", value);
                }}
                error={store.errors.values.phone_number}
              />
            </div>
            <InputField
              label={t("Default password")}
              name="password"
              type="password"
              canToggleType={true}
              value={form.values.password}
              onChange={form.handleChange}
              error={store.errors.values.password}
            />
          </Show>

          <Show when={useExistingUser as boolean}>
            <SelectUser
              user={user}
              onUserSelected={(user: any | undefined) => {
                setUser(user);
                form.setValue("user_id", user?.id);
              }}
            />
          </Show>

          {/* Employee Info */}
        </div>
      </div>

      <div className="mt-8 bg-white p-6 rounded-md border-gray-200">
        <h3 className="font-semibold">{t("Employment informations")}</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <LightSelectField
            label={t("Role")}
            name="role"
            value={form.values.role}
            onChange={form.handleChange}
            error={store.errors.values.role}
            options={EmployeeRoleOptions.filter(
              (item) => item.value != "shop_owner"
            )}
          />

          <InputField
            label={t("Date of Birth")}
            name="date_of_birth"
            type="date"
            value={form.values.date_of_birth}
            onChange={form.handleChange}
            error={store.errors.values.date_of_birth}
          />

          <InputField
            label={t("Hire Date")}
            name="hire_date"
            type="date"
            value={form.values.hire_date}
            onChange={form.handleChange}
            error={store.errors.values.hire_date}
          />
          <SelectField
            label={t("Status")}
            name="status"
            value={form.values.status}
            onChange={form.handleChange}
            error={store.errors.values.status}
            options={translatedStatusOptions}
          />
          <SelectField
            label={t("Employment Status")}
            name="employment_status"
            value={form.values.employment_status}
            onChange={form.handleChange}
            error={store.errors.values.employment_status}
            options={employmentStatusOptions}
          />

          <div>
            <label
              className={cn("mb-1.5 block text-sm font-medium text-gray-900")}
            >
              {t("Emergency contact")}
            </label>
            <PhoneNumberField
              value={form.values.emergency_contact}
              onValueChange={(value: string) => {
                form.setValue("emergency_contact", value);
              }}
              error={store.errors.values.emergency_contact}
            />
          </div>

          <InputField
            label={t("Salary")}
            name="salary"
            type="number"
            value={form.values.salary}
            onChange={form.handleChange}
            error={store.errors.values.salary}
          />
        </div>
      </div>

      <Button className="mt-6 px-6 py-2" onClick={submit}>
        {t("Register the employee")}
      </Button>
    </section>
  );
}
